import React, { useEffect, useState } from "react";
import { Plus, DoorOpen, Calculator, CheckCircle2, ClipboardList } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtDate, fmtMoney, EmptyState } from "../components/ui";

export default function Exit() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employeeId: "", type: "RESIGNATION", reason: "", lastWorkingDay: "" });
  const [settle, setSettle] = useState(null);
  const [interview, setInterview] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [ivMeta, setIvMeta] = useState({ overallRating: 3, wouldRecommend: true });
  const canManage = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);
  const isHR = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/exit").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/employees?limit=100").then(({ data }) => setEmps(data.items)); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/exit", form); setOpen(false); setForm({ employeeId: "", type: "RESIGNATION", reason: "", lastWorkingDay: "" }); load(); };
  const toggleItem = async (x, i, done) => { await api.put(`/exit/${x.id}`, { itemIndex: i, done }); load(); };
  const setStatus = async (x, status) => { await api.put(`/exit/${x.id}`, { status }); load(); };
  const viewSettle = async (x) => { const { data } = await api.get(`/exit/${x.id}/settlement`); setSettle({ x, data }); };
  const complete = async (x) => { if (!window.confirm("Mark exit as completed? This terminates the employee.")) return; try { await api.post(`/exit/${x.id}/complete`); load(); } catch (e) { alert(e.response?.data?.detail || "Failed"); } };
  const openInterview = async (x) => {
    if (x.interview) { setInterview({ x, existing: x.interview }); return; }
    const { data } = await api.get("/exit/interview-questions");
    setAnswers(data.questions.map((q) => ({ question: q, answer: "" })));
    setIvMeta({ overallRating: 3, wouldRecommend: true });
    setInterview({ x });
  };
  const saveInterview = async (e) => {
    e.preventDefault();
    try { await api.post(`/exit/${interview.x.id}/interview`, { responses: answers, ...ivMeta }); alert("Exit interview recorded"); setInterview(null); load(); }
    catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };
  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader title="Exit Management" subtitle="Offboarding, clearance & full-and-final settlement">
        {canManage && <button data-testid="add-exit" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Initiate Exit</button>}
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={DoorOpen} title="No active exits" /> : (
        <div className="space-y-4">
          {items.map((x) => {
            const citems = x.clearanceItems || [];
            const doneCount = citems.filter((c) => c.status === "COMPLETED").length;
            return (
              <Card key={x.id} className="p-6" data-testid={`exit-${x.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-head text-lg font-bold text-slate-900 dark:text-white">{x.employeeName}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{x.type} · LWD {fmtDate(x.lastWorkingDay)} · Notice {x.noticePeriod || 30}d</p>
                    {x.reason && <p className="mt-1 text-sm text-slate-500">{x.reason}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge value={x.status} tone={x.status === "COMPLETED" ? "ok" : "warn"} />
                    {canManage && <button onClick={() => openInterview(x)} data-testid={`interview-${x.id}`} className="btn-outline !py-1.5 text-xs"><ClipboardList size={14} /> {x.interview ? "Interview ✓" : "Exit Interview"}</button>}
                    {isHR && <button onClick={() => viewSettle(x)} data-testid={`settle-${x.id}`} className="btn-outline !py-1.5 text-xs"><Calculator size={14} /> F&F</button>}
                    {isHR && x.status !== "COMPLETED" && <button onClick={() => complete(x)} data-testid={`complete-${x.id}`} className="btn-accent !py-1.5 text-xs"><CheckCircle2 size={14} /> Complete</button>}
                  </div>
                </div>
                {citems.length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Clearance Checklist · {doneCount}/{citems.length}</p>
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      {citems.map((c, i) => (
                        <button key={i} disabled={!canManage} onClick={() => toggleItem(x, i, c.status !== "COMPLETED")} data-testid={`clear-item-${x.id}-${i}`}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs ${c.status === "COMPLETED" ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10" : "border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}>
                          <span>{c.status === "COMPLETED" ? "✓" : "○"}</span>
                          <span><b className="text-[10px] uppercase opacity-60">{c.department}</b> · {c.item}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Initiate Exit"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="exit-form" className="btn-accent">Initiate</button></>}>
        <form id="exit-form" onSubmit={add} className="space-y-4">
          <div><label className="label">Employee *</label><select required data-testid="exit-emp" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className="input"><option value="">— Select —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">{["RESIGNATION", "TERMINATION", "RETIREMENT"].map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="label">Last Working Day</label><input type="date" data-testid="exit-lwd" value={form.lastWorkingDay} onChange={(e) => setForm({ ...form, lastWorkingDay: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Reason</label><textarea data-testid="exit-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="input h-20 py-2" /></div>
        </form>
      </Modal>

      {settle && (
        <Modal open onClose={() => setSettle(null)} title={`F&F Settlement · ${settle.x.employeeName}`}>
          <div className="space-y-3" data-testid="settlement-detail">
            <div className="space-y-1">
              {settle.data.earnings.map((e, i) => <div key={i} className="flex justify-between text-sm"><span className="text-slate-500">{e.label}</span><span className="font-mono text-emerald-600">+{fmtMoney(e.amount)}</span></div>)}
              {settle.data.deductions.map((d, i) => <div key={i} className="flex justify-between text-sm"><span className="text-slate-500">{d.label}</span><span className="font-mono text-red-600">−{fmtMoney(d.amount)}</span></div>)}
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-3 dark:border-slate-700"><span className="font-semibold">Net Settlement</span><span className="font-mono text-lg font-bold text-slate-900 dark:text-white">{fmtMoney(settle.data.net)}</span></div>
            <p className="text-[11px] text-slate-400">Based on monthly gross ₹{settle.data.grossMonthly.toLocaleString("en-IN")} (₹{settle.data.perDay}/day) and {settle.data.remainingLeaveDays} encashable leave days.</p>
          </div>
        </Modal>
      )}

      {interview && (
        <Modal open onClose={() => setInterview(null)} title={`Exit Interview · ${interview.x.employeeName}`}
          footer={!interview.existing && <><button onClick={() => setInterview(null)} className="btn-outline">Cancel</button><button form="iv-form" className="btn-accent">Save Interview</button></>}>
          {interview.existing ? (
            <div className="space-y-3" data-testid="interview-readonly">
              {(interview.existing.responses || []).map((r, i) => (
                <div key={i}><p className="text-xs font-semibold text-slate-500">{r.question}</p><p className="text-sm text-slate-800 dark:text-slate-100">{r.answer || "—"}</p></div>
              ))}
              <div className="flex gap-4 border-t border-slate-200 pt-3 text-sm dark:border-slate-700">
                <span>Rating: <b>{interview.existing.overallRating || "—"}/5</b></span>
                <span>Would recommend: <b>{interview.existing.wouldRecommend ? "Yes" : "No"}</b></span>
              </div>
              <p className="text-[11px] text-slate-400">Conducted by {interview.existing.conductedBy} · {fmtDate(interview.existing.conductedAt)}</p>
            </div>
          ) : (
            <form id="iv-form" onSubmit={saveInterview} className="space-y-4" data-testid="interview-form">
              {answers.map((a, i) => (
                <div key={i}><label className="label">{a.question}</label>
                  <textarea data-testid={`iv-answer-${i}`} value={a.answer} onChange={(e) => setAnswers(answers.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))} className="input h-16 py-2" /></div>
              ))}
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Overall experience (1–5)</label>
                  <select data-testid="iv-rating" value={ivMeta.overallRating} onChange={(e) => setIvMeta({ ...ivMeta, overallRating: +e.target.value })} className="input">{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
                <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="iv-recommend" checked={ivMeta.wouldRecommend} onChange={(e) => setIvMeta({ ...ivMeta, wouldRecommend: e.target.checked })} /> Would recommend SMIFS</label>
              </div>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}
